'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { Cloud, Cpu, Receipt } from 'lucide-react';
import SplitReveal from '@/components/motion/SplitReveal';
import Eyebrow from '@/components/ui/Eyebrow';
import ThroughputChart from './ThroughputChart';
import { DeployVisual, FeatureStyles, PricingVisual, WorkflowVisual } from './FeatureVisuals';

// Three reasons, not three steps — so they carry icons, not numbers. All three
// are laid out open: nobody should have to click to read them.
const features = [
  {
    icon: Cpu,
    visual: WorkflowVisual,
    title: 'Designed for the Digital Era',
    content:
      'Why use an LMS built prior to digital lenses as opposed to one built for the digital age? LenSync was built with the future and customization in mind; using modern code vs those burdened with legacy software allows our labs to create the system they need to serve their customers.',
  },
  {
    icon: Cloud,
    visual: DeployVisual,
    title: 'Server Based or Cloud Versions',
    content:
      'We offer flexible deployment options to suit your business needs. Choose between our robust server-based solution for complete control or our cloud version for accessibility from anywhere.',
  },
  {
    icon: Receipt,
    visual: PricingVisual,
    title: 'Simplified Pricing Structure',
    content:
      'Our transparent pricing model eliminates hidden fees and complex structures. Pay only for what you need with predictable costs that scale with your business.',
  },
];

const EASE = [0.22, 1, 0.36, 1];
const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const FeaturesSection = (props) => {
  return (
    <section id={props.id} className="relative overflow-hidden py-24 sm:py-32">
      {/* ambient glow — brand blue + mint, no purple */}
      <div className="pointer-events-none absolute -left-24 top-16 size-72 rounded-full bg-primary-blue/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 bottom-8 size-72 rounded-full bg-emerald-200/30 blur-3xl" />

      <div className="container relative mx-auto px-5">
        {/* the claim, and the chart of it */}
        <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-stretch lg:gap-14">
          <div className="lg:py-4">
            <Eyebrow className="mb-5">Features</Eyebrow>
            <SplitReveal className="display text-[clamp(32px,3.7vw,58px)] text-secondary-dark">
              Same Amount of Employees to Process
              <span className="block text-primary-blue">35% More Jobs Per Day</span>
            </SplitReveal>
            <p className="mt-6 max-w-xl text-lg text-gray-500">
              Barcoding at every station means the system makes all pertinent decisions so the
              employees can focus on customer service and production vs returning calls for missing
              data or re-ordering lens blanks. All the edging parameters are determined at the time
              the order is created!
            </p>
          </div>

          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
          >
            <ThroughputChart />
          </motion.div>
        </div>

        {/* three reasons, all open, one after another */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          transition={{ staggerChildren: 0.14 }}
          className="mt-16 grid gap-5 md:grid-cols-3 lg:mt-20 lg:gap-6"
        >
          {features.map((feature) => {
            const Icon = feature.icon;
            const Visual = feature.visual;
            return (
              <motion.article
                key={feature.title}
                variants={reveal}
                className="flex flex-col rounded-[1.5rem] border border-secondary-dark/[0.07] bg-white p-4 shadow-[0_30px_80px_-40px_rgba(2,32,71,0.3)]"
              >
                <Visual />
                <div className="flex flex-1 flex-col px-2 pb-3 pt-6">
                  <div className="flex items-center gap-3">
                    <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-primary-blue text-white">
                      <Icon className="size-[18px]" strokeWidth={1.75} />
                    </span>
                    <h3 className="text-xl font-semibold leading-tight tracking-tight text-secondary-dark">
                      {feature.title}
                    </h3>
                  </div>
                  <p className="mt-4 leading-relaxed text-gray-500">{feature.content}</p>
                </div>
              </motion.article>
            );
          })}
        </motion.div>
      </div>

      <FeatureStyles />
    </section>
  );
};

export default FeaturesSection;
